import { MeansOfTransport } from '../transportAssignment.types';
import { Interval, isAfter, isBefore, isWithinInterval } from 'date-fns';
import { dateMinusSeconds, isDateStringEmpty, isValidDate } from '../../sharedComponents/dateHelper';
import { GRACE_PERIOD_IN_SECONDS_COMPLETION_DATE } from '../../sharedComponents/commonConfig';
import { ValidationResult } from '../../sharedComponents/validationResult.type';

const MAX_MEANS_OF_TRANSPORT_ID_LENGTH = 35;

export const getEmptyValidationResult = (): ValidationResult => ({ error: undefined, warning: undefined });

export const keepFirstValidationMessage = (a: ValidationResult, b: ValidationResult): ValidationResult => ({
    error: a.error ?? b.error,
    warning: a.warning ?? b.warning,
});

export const validateMeansOfTransportLength = (meansOfTransport: MeansOfTransport | undefined) =>
    meansOfTransport?.id !== undefined && MAX_MEANS_OF_TRANSPORT_ID_LENGTH < meansOfTransport.id.length
        ? {
              warning: undefined,
              error: 'outboundOrderBook.transportAssignments.sidebar.confirmationForm.meansOfTransport.feedbackLengthError',
          }
        : getEmptyValidationResult();

export const validateTrailerIdLength = (meansOfTransport: MeansOfTransport | undefined) =>
    meansOfTransport?.trailerId !== undefined && MAX_MEANS_OF_TRANSPORT_ID_LENGTH < meansOfTransport.trailerId.length
        ? {
              warning: undefined,
              error: 'outboundOrderBook.transportAssignments.sidebar.confirmationForm.meansOfTransport.feedbackLengthError',
          }
        : getEmptyValidationResult();
export const PICKUP_DATE_VALIDATION_MESSAGES = {
    invalidDate: 'outboundPortal.common.invalidDate',
    beforeRequestedPickUpInterval: 'outboundPortal.transportAssignments.sidebar.dateBeforeRequestedPickUpInterval',
    notWithinRequestedTimeframe:
        'outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedPickUpDate.isNotWithinRequestedPickupTimeframe',
    missing: 'outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedPickUpDate.feedbackWarning',
};
export const DELIVERY_DATE_VALIDATION_MESSAGES = {
    invalidDate: 'outboundPortal.common.invalidDate',
    beforeOrderDate: 'outboundPortal.transportAssignments.sidebar.dateBeforeReceivedError',
    afterDeliveryDate:
        'outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedDeliveryDate.isAfterRequestedDeliveryDate',
    missing: 'outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedDeliveryDate.feedbackWarning',
};
export const validatePickUpDate = (
    requestedPickUpDate: Interval,
    confirmedPickUpDate: Date | string | undefined,
): ValidationResult => {
    if (isDateStringEmpty(confirmedPickUpDate)) {
        return { error: undefined, warning: undefined };
    }
    if (!isValidDate(confirmedPickUpDate)) {
        return { error: PICKUP_DATE_VALIDATION_MESSAGES.invalidDate, warning: undefined };
    }

    if (
        isAfter(
            dateMinusSeconds(requestedPickUpDate.start as Date, GRACE_PERIOD_IN_SECONDS_COMPLETION_DATE),
            confirmedPickUpDate,
        )
    ) {
        return { error: PICKUP_DATE_VALIDATION_MESSAGES.beforeRequestedPickUpInterval, warning: undefined };
    }
    if (!isWithinInterval(confirmedPickUpDate, requestedPickUpDate)) {
        return { error: undefined, warning: PICKUP_DATE_VALIDATION_MESSAGES.notWithinRequestedTimeframe };
    }

    return { error: undefined, warning: undefined };
};
export const validateDeliveryDate = (
    assignment: {
        readonly orderIssuedAt: Date;
        readonly requestedDeliveryDate: Date | undefined;
    },
    confirmedDeliveryDate: Date | string | undefined,
): ValidationResult => {
    if (isDateStringEmpty(confirmedDeliveryDate)) {
        return { error: undefined, warning: undefined };
    }
    if (!isValidDate(confirmedDeliveryDate)) {
        return { error: DELIVERY_DATE_VALIDATION_MESSAGES.invalidDate, warning: undefined };
    }
    if (
        isAfter(
            dateMinusSeconds(assignment.orderIssuedAt, GRACE_PERIOD_IN_SECONDS_COMPLETION_DATE),
            confirmedDeliveryDate,
        )
    ) {
        return { error: DELIVERY_DATE_VALIDATION_MESSAGES.beforeOrderDate, warning: undefined };
    }

    if (
        assignment.requestedDeliveryDate !== undefined &&
        isAfter(confirmedDeliveryDate, assignment.requestedDeliveryDate)
    ) {
        return { error: undefined, warning: DELIVERY_DATE_VALIDATION_MESSAGES.afterDeliveryDate };
    }

    return { error: undefined, warning: undefined };
};
export const getExecutionStatusDateErrorMessage = (
    date: Date | string | undefined,
    orderIssuedAt: Date,
    loadingDate?: Date | string,
): string | undefined => {
    const isInTheFuture = isValidDate(date) && date > new Date();
    const statusDateBeforeReceived =
        isValidDate(date) && isAfter(dateMinusSeconds(orderIssuedAt, GRACE_PERIOD_IN_SECONDS_COMPLETION_DATE), date);
    const isBeforeLoadingDate = isValidDate(date) && isValidDate(loadingDate) && isBefore(date, loadingDate);

    if (isInTheFuture) return 'outboundPortal.transportAssignments.sidebar.executionStatus.inTheFuture';
    if (statusDateBeforeReceived) return 'outboundPortal.transportAssignments.sidebar.dateBeforeReceivedError';
    if (isBeforeLoadingDate)
        return 'outboundPortal.transportAssignments.sidebar.executionStatus.unloadingBeforeLoadingDate';
    if (date === '' || !isValidDate(date)) return 'outboundPortal.common.invalidDate';
    return undefined;
};
